import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { DeviceToken, User } from '../../types/User';
import ContentHeader from './components/ContentHeader';
import Groups from './components/Groups';
import Tokens from './components/Tokens';
import Email from './components/Email';
import UserDrawerContentLoader from './components/UserDrawerContentLoader';
import { DefaultFooterComponent, showAlert } from '@silverfort/sf-components';
import { useUpdateUser } from '../../api';
import { useSnackAlert } from '../../context/SnackAlertProvider';
import { getUpdateUserDetails, getUserDrawerFormInitialValuesFromUserDetails } from './utils';
import { FormProvider, useForm } from 'react-hook-form';
import { UserDrawerFormValues } from '../../types/forms';
import { isEmpty } from 'lodash';
import DetailsSection from './components/DetailsSection';
import { UserSession } from '../../../../api';
import { useGetUserDetailsByGuid } from '../../api/queries/useGetUserDetailsByGuid';
import { SystemData } from '../../types/SystemData';

interface UserDrawerContentProps {
    user: User;
    tokenToFocus?: DeviceToken['id'];
    onClose: () => void;
    onUserUpdated: () => void;
    userSession: UserSession;
    systemData?: SystemData;
}

const overflowScrollbarStyles = `[scrollbar-width:thin] [scrollbar-color:rgba(0,0,0,0.2)_transparent]
 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-transparent
 [&::-webkit-scrollbar-thumb]:bg-black/20 [&::-webkit-scrollbar-thumb]:rounded-sm
 [&::-webkit-scrollbar-thumb:hover]:bg-black/30`;

const UserDrawerContent: FC<UserDrawerContentProps> = ({
    user,
    tokenToFocus,
    onClose,
    onUserUpdated,
    userSession,
    systemData
}) => {
    const [tokens, setTokens] = useState(user.tokens);
    const userDrawerForm = useForm<UserDrawerFormValues>({
        defaultValues: getUserDrawerFormInitialValuesFromUserDetails(user),
        mode: 'onBlur'
    });
    const {
        handleSubmit,
        formState: { isDirty, errors, dirtyFields },
        reset
    } = userDrawerForm;
    const isValid = isEmpty(errors);
    const { openSnackAlert } = useSnackAlert();
    const lastSavedValuesRef = useRef<UserDrawerFormValues | null>(null);

    const {
        secondaryUsers = [],
        groups = [],
        loading,
        localAccounts = [],
        localAccountsCount
    } = useGetUserDetailsByGuid({ userGuid: user.guid, options: { includeLocalAccounts: true } }); // todo: handle errors after design from Sarai - https://silverfort.atlassian.net/browse/AF-38308

    const allGroups = useMemo(() => {
        const secondaryUsersGroups = secondaryUsers.flatMap((user) => user.groups);
        const uniqueGroups = new Set([...groups, ...secondaryUsersGroups]);

        return Array.from(uniqueGroups);
    }, [groups, secondaryUsers]);

    const handleUpdateUserSuccess = useCallback(() => {
        openSnackAlert({
            message: `User ${user.displayName} updated successfully`
        });
        if (lastSavedValuesRef.current) {
            reset(lastSavedValuesRef.current);
        }
        onUserUpdated();
    }, [user.displayName, onUserUpdated, openSnackAlert, reset]);

    const handleUpdateUserError = useCallback(() => {
        showAlert('Failed to update user, please try again', 'error');
    }, []);

    const { updateUser } = useUpdateUser({
        onSuccess: handleUpdateUserSuccess,
        onError: handleUpdateUserError
    });

    const handleUpdateUserFromHeader = useCallback(() => {
        onUserUpdated();
        onClose();
    }, [onUserUpdated, onClose]);

    const handleSave = async (formValues: UserDrawerFormValues) => {
        const updateUserDetails = getUpdateUserDetails(formValues, user, dirtyFields);
        lastSavedValuesRef.current = formValues;
        updateUser(user.guid, updateUserDetails);
    };

    const handleTokenRemoved = useCallback(
        (tokenId: string) => {
            setTokens((prevTokens) => prevTokens.filter((token) => token.id !== tokenId));

            onUserUpdated();
        },
        [onUserUpdated]
    );

    const primaryUserTokens = useMemo(
        () => ({
            displayName: user.displayName,
            guid: user.guid,
            tokens
        }),
        [tokens, user.displayName, user.guid]
    );

    const secondaryUsersToToken = useMemo(() => {
        return secondaryUsers.map((user) => ({
            displayName: user.displayName,
            guid: user.guid,
            tokens: user.tokens
        }));
    }, [secondaryUsers]);

    if (loading) {
        return <UserDrawerContentLoader />;
    }

    return (
        <FormProvider {...userDrawerForm}>
            <div className="w-full h-full flex flex-col justify-between">
                <div
                    className={`flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-4 ${overflowScrollbarStyles}`}>
                    <ContentHeader
                        user={user}
                        userSession={userSession}
                        secondaryUsers={secondaryUsers}
                        boundLocalAccounts={localAccounts}
                        boundLocalAccountsCount={localAccountsCount}
                        onUserUpdated={handleUpdateUserFromHeader}
                    />
                    <div className="p-4 flex flex-col gap-6">
                        <DetailsSection user={user} />
                        <div className="border-b-2 border-sf-gray-100 border-dashed" />
                        <Groups groups={allGroups} />
                        <Tokens
                            primaryUserToTokens={primaryUserTokens}
                            secondaryUsersToToken={secondaryUsersToToken}
                            tokenToFocus={tokenToFocus}
                            onTokenRemoved={handleTokenRemoved}
                            userSession={userSession}
                        />
                        <Email
                            adEmail={user.adEmail}
                            userSession={userSession}
                            userGuid={user.guid}
                            systemData={systemData}
                        />
                    </div>
                </div>
                <div className="border-t border-sf-gray-100 min-h-16 px-6 flex items-center">
                    <DefaultFooterComponent
                        onCancel={onClose}
                        onApprove={handleSubmit(handleSave)}
                        primaryButtonProps={{
                            disabled: !isValid || !isDirty
                        }}
                    />
                </div>
            </div>
        </FormProvider>
    );
};

export default UserDrawerContent;
